// ProfileModal.jsx
import React, { useState, useEffect } from "react";
import { getFirestore, doc, setDoc } from "firebase/firestore";

const defaultAvatar = "/default-avatar.png";

/* ---------- GOOGLE DRIVE CONVERTER ---------- */
const convertGoogleDriveUrl = (url) => {
  if (!url) return "";

  let match = url.match(/\/d\/([^/]+)/);
  if (!match) match = url.match(/[?&]id=([^&]+)/);

  return match ? `https://lh3.googleusercontent.com/d/${match[1]}` : url;
};

export default function ProfileModal({
  show,
  onClose,
  currentImage,
  onUpdate,
  userId,
}) {
  const [previewUrl, setPreviewUrl] = useState(currentImage || defaultAvatar);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const [imageSource, setImageSource] = useState("cloudinary"); // cloudinary | drive
  const [driveUrl, setDriveUrl] = useState("");

  useEffect(() => {
    setPreviewUrl(currentImage || defaultAvatar);
  }, [currentImage]);

  /* ---------- SAVE IMAGE ---------- */
  const saveImageUrl = async (imageUrl) => {
    try {
      setUploading(true);
      const db = getFirestore();
      await setDoc(
        doc(db, "adminUsers", userId),
        { profileImageUrl: imageUrl },
        { merge: true }
      );

      onUpdate(imageUrl);
      setPreviewUrl(imageUrl);
      setError("");
      onClose();
    } catch {
      setError("Error saving profile image.");
    } finally {
      setUploading(false);
    }
  };

  /* ---------- CLOUDINARY ---------- */
  const openUploadWidget = () => {
    if (!window.cloudinary) {
      setError("Cloudinary widget is not loaded.");
      return;
    }

    const widget = window.cloudinary.createUploadWidget(
      {
        cloudName: "dgxhp09em",
        uploadPreset: "unsigned_preset",
        multiple: false,
        folder: "adminProfileIcons",
        sources: ["local", "camera", "url"],
        maxFileSize: 2000000,
        clientAllowedFormats: ["png", "jpg", "jpeg", "gif"],
      },
      async (error, result) => {
        if (error) {
          setError("Upload error.");
          return;
        }
        if (result?.event === "success") {
          await saveImageUrl(result.info.secure_url);
        }
      }
    );

    widget.open();
  };

  /* ---------- GOOGLE DRIVE SAVE ---------- */
  const handleDriveSave = async () => {
    if (!driveUrl.trim()) {
      setError("Please paste a Google Drive image link.");
      return;
    }

    const converted = convertGoogleDriveUrl(driveUrl);
    setPreviewUrl(converted);
    await saveImageUrl(converted);
  };

  if (!show) return null;

  return (
    <div
      className="modal fade show"
      style={{
        display: "block",
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.3)",
        zIndex: 2000,
      }}
    >
      <div className="modal-dialog" style={{ marginTop: "10vh", maxWidth: 420 }}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Change Profile Icon</h5>
            <button
              className="btn-close"
              onClick={onClose}
              disabled={uploading}
            />
          </div>

          <div className="modal-body text-center">
            <img
              src={previewUrl}
              alt="Preview"
              style={{
                width: 100,
                height: 100,
                borderRadius: "50%",
                objectFit: "cover",
                border: "2px solid #ddd",
                marginBottom: 16,
              }}
            />

            {error && (
              <div className="alert alert-danger py-1">{error}</div>
            )}

            {/* IMAGE SOURCE SELECT */}
            <select
              className="form-select mb-3"
              value={imageSource}
              onChange={(e) => {
                setImageSource(e.target.value);
                setError("");
              }}
            >
              <option value="cloudinary">Upload via Cloudinary</option>
              <option value="drive">Use Google Drive Image</option>
            </select>

            {/* CLOUDINARY */}
            {imageSource === "cloudinary" && (
              <button
                className="btn btn-primary w-100"
                onClick={openUploadWidget}
                disabled={uploading}
              >
                {uploading ? "Uploading..." : "Upload Image"}
              </button>
            )}

            {/* GOOGLE DRIVE */}
            {imageSource === "drive" && (
              <>
                <input
                  className="form-control mb-2"
                  placeholder="Paste Google Drive image link"
                  value={driveUrl}
                  onChange={(e) => setDriveUrl(e.target.value)}
                />
                <button
                  className="btn btn-success w-100"
                  onClick={handleDriveSave}
                  disabled={uploading}
                >
                  Use This Image
                </button>
                <small className="text-muted d-block mt-2">
                  Link must be public (Anyone with link → Viewer)
                </small>
              </>
            )}
          </div>

          <div className="modal-footer">
            <button
              className="btn btn-secondary"
              onClick={onClose}
              disabled={uploading}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
